import type { Passage } from './passages';

/*
 * ONE FROM EACH, IN TURN — how a figure's passages are ordered.
 *
 * Theirs buckets a figure's passages by scripture and then takes one from each in rotation
 * (engine.js:1993), and says so to the reader: *ordered one from each, in turn*. Ours grouped them:
 * the lead scripture's passages first, then every other scripture's, each block whole.
 *
 * ON THIS MAP THAT IS THE DIFFERENCE BETWEEN COMPARING AND NOT. Adam is named in 21 Hebrew Bible
 * passages, 7 in the New Testament, 26 in the Quran, 35 in hadith and 1 in the Mishnah. Grouped, a
 * reader meets twenty-one Hebrew Bible verses before the first Quranic one and has to go looking for
 * the comparison. Woven, the traditions stand side by side from the first screen — which is the
 * whole reason this library was built.
 *
 * THE LEAD SCRIPTURE STILL LEADS. It is not the only one shown first any more; it is where the
 * rotation STARTS, which is their `lens` exactly. A reader who reads from the Quran opens on the
 * Quran and then sees what the others say beside it, rather than opening on the Quran and then
 * reading the whole Quran.
 *
 * It only REORDERS, and it never drops one: filtering is the chips' job, and within each scripture
 * the reference order is untouched. Both are asserted, because an ordering that silently loses a
 * passage would be indistinguishable from a library that does not hold it.
 */

/**
 * The scriptures present, in the order they first appear.
 *
 * Derived rather than declared: the passages arrive grouped and canonical, so first appearance IS
 * the registry order, and a hard-coded list here would be a second copy of it that nothing keeps in
 * step — the exact failure this project has a rule against.
 */
function corporaIn(passages: readonly Passage[]): string[] {
  const seen: string[] = [];
  for (const passage of passages) if (!seen.includes(passage.corpusId)) seen.push(passage.corpusId);
  return seen;
}

export function sortPassages(passages: Passage[], lead: string | null): Passage[] {
  const order = corporaIn(passages);
  if (order.length < 2) return passages;

  /* The rotation starts at the lead, if the lead is one of the scriptures actually present. */
  const at = lead === null ? -1 : order.indexOf(lead);
  const rotated = at >= 0 ? [...order.slice(at), ...order.slice(0, at)] : order;

  const buckets = new Map<string, Passage[]>();
  for (const passage of passages) {
    const bucket = buckets.get(passage.corpusId);
    if (bucket) bucket.push(passage); else buckets.set(passage.corpusId, [passage]);
  }

  const woven: Passage[] = [];
  let taken = true;
  while (taken) {
    taken = false;
    for (const corpus of rotated) {
      const bucket = buckets.get(corpus);
      const next = bucket?.shift();
      if (next !== undefined) { woven.push(next); taken = true; }
    }
  }
  return woven;
}
